/**
 * ===================================================
 *  MAP PRICING SCRIPT (v6)
 *
 *  SEARCH RESULTS + SEARCH DROPDOWN (.gl-card):
 *   - Product has map_price (from #map-product-data)
 *       -> show map_price + "Add to Cart for the Lowest Price Ever" message
 *   - Product has NO map_price
 *       -> fetch /products/<handle>.js and show FIRST VARIANT price
 *          (compare-at x'd out if the variant has one), NO message
 *
 *  COLLECTION PAGES (.spf-product-card):
 *   - map_price -> show it + message, hide old price
 *   - no map_price -> untouched
 * ===================================================
 */
(function () {
  'use strict';

  // ---------- CONFIG ----------
  const CARD_TYPES = [
    {
      // Search results page + search dropdown (same .gl-card markup)
      name: 'search',
      mode: 'box',
      card: '.gl-card',
      title: '.gl-card-title a',
      link: '.gl-card-title a',
      priceBox: '.gl-card-pricing'
    },
    {
      // Category / collection pages (unchanged behaviour)
      name: 'collection',
      mode: 'inline',
      card: '.spf-product-card',
      title: '.spf-product-card__title, .spf-product-card__title a',
      link: 'a[href*="/products/"]',
      price: '.spf-product-card__saleprice',
      oldPrice: '.spf-product-card__oldprice',
      noteAfter: '.spf-product-card__price-wrapper'
    }
  ];

  const MAP_PRICING_TEXT =
    'Add to Cart for the Lowest Price Ever! Exclusive online pricing revealed in Cart!';

  const ALL_CARD_SELECTOR = CARD_TYPES.map(t => t.card).join(',');

  // ---------- State ----------
  let cachedProducts = null;
  let skuMap = null;
  let titleMap = null;
  let mapsBuiltFromCount = -1;
  const origBoxHTML = new WeakMap();   // priceBox -> original innerHTML
  const variantCache = new Map();      // handle -> Promise(first variant | null)

  // ---------- CSS ----------
  function injectCSS() {
    if (document.getElementById('map-pricing-style')) return;
    const style = document.createElement('style');
    style.id = 'map-pricing-style';
    style.textContent = `
      .map-hide-old { display: none !important; }
      .map-pricing-note {
        font-size: 12px;
        background: #F9F4F4;
        color: #F49342;
        margin-top: 4px;
        font-weight: 600;
        padding-left: 6px;
        line-height: normal;
      }
    `;
    document.head.appendChild(style);
  }

  // ---------- Extract #map-product-data ----------
  function extractAllProductData() {
    const el = document.getElementById('map-product-data');
    if (!el) return [];
    const rawText = el.textContent.trim();
    if (!rawText) return [];

    let rawList = [];
    try { rawList = JSON.parse(rawText); }
    catch (e) { rawList = rawText.split(/\r?\n/); }

    const out = [];
    for (let i = 0; i < rawList.length; i++) {
      const rawString = String(rawList[i]).trim();
      if (!rawString || rawString === '[' || rawString === ']') continue;
      const cleaned = rawString.replace(/^["']|["'],?$/g, '');
      const [title, productId, sku, variantId, price] = cleaned.split('____');
      if (title && productId) {
        out.push({
          title: title.trim(),
          productId: productId.trim(),
          sku: sku ? sku.trim() : '',
          variantId: variantId ? variantId.trim() : '',
          price: price ? price.trim() : ''
        });
      }
    }
    return out;
  }

  // ---------- Normalize helpers ----------
  function normalizeTitle(str) {
    return String(str || '').trim().toLowerCase().replace(/\s+/g, ' ').replace(/[^\w\s]/g, '');
  }
  function normalizeCode(str) {
    return String(str || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  }
  function codesFromTitle(title) {
    const m = String(title || '').match(/\b[A-Za-z]{1,4}\d{2,6}[A-Za-z0-9]*\b/g) || [];
    return m.map(normalizeCode).sort((a, b) => b.length - a.length);
  }
  function codesFromHref(href) {
    if (!href) return [];
    const slug = href.split('?')[0].split('#')[0].split('/').pop() || '';
    const m = slug.match(/[a-z]{1,4}\d{2,6}[a-z0-9]*/gi) || [];
    return m.map(normalizeCode).sort((a, b) => b.length - a.length);
  }
  function handleFromHref(href) {
    const m = String(href || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ---------- Build maps ----------
  function buildProductMaps() {
    if (!cachedProducts || cachedProducts.length === 0) {
      cachedProducts = extractAllProductData();
    }
    if (cachedProducts.length === 0) return false;
    if (skuMap && mapsBuiltFromCount === cachedProducts.length) return true;

    skuMap = new Map();
    titleMap = new Map();
    cachedProducts.forEach(p => {
      if (p.sku) {
        const k = normalizeCode(p.sku);
        if (!skuMap.has(k)) skuMap.set(k, p);
      }
      if (p.title) {
        const k = normalizeTitle(p.title);
        if (!titleMap.has(k)) titleMap.set(k, p);
      }
    });
    mapsBuiltFromCount = cachedProducts.length;
    return true;
  }

  function findMatch(cardTitle, cardHref) {
    if (!skuMap) return null;
    for (const code of codesFromTitle(cardTitle)) {
      if (skuMap.has(code)) return skuMap.get(code);
    }
    for (const code of codesFromHref(cardHref)) {
      if (skuMap.has(code)) return skuMap.get(code);
    }
    const nt = normalizeTitle(cardTitle);
    if (nt && titleMap.has(nt)) return titleMap.get(nt);
    return null;
  }

  // ---------- Price helpers ----------
  function formatPrice(raw) {
    const s = String(raw === undefined || raw === null ? '' : raw).trim();
    if (!s) return '';
    const n = parseFloat(s.replace(/[^0-9.]/g, ''));
    if (isNaN(n) || n <= 0) return '';
    return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Shopify .js endpoint returns cents (integer); handle "159.95" style too
  function centsToDollars(v) {
    if (v === null || v === undefined || v === '') return 0;
    if (typeof v === 'number') return v / 100;
    const s = String(v);
    return s.indexOf('.') !== -1 ? parseFloat(s) : parseFloat(s) / 100;
  }

  // ---------- First variant fetch ----------
  function getFirstVariant(handle) {
    if (variantCache.has(handle)) return variantCache.get(handle);

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const p = fetch(root + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' }
    })
      .then(r => (r.ok ? r.json() : null))
      .then(data => (data && data.variants && data.variants[0]) || null)
      .catch(() => null);

    variantCache.set(handle, p);
    return p;
  }

  // ---------- Note helpers ----------
  function removeNotes(card) {
    card.querySelectorAll('.map-pricing-note').forEach(n => n.remove());
  }
  function addNoteAfter(card, anchor) {
    if (card.querySelector('.map-pricing-note')) return;
    const note = document.createElement('div');
    note.className = 'map-pricing-note';
    note.textContent = MAP_PRICING_TEXT;
    anchor.insertAdjacentElement('afterend', note);
  }

  // =====================================================
  //  MODE "box": search results + dropdown (.gl-card)
  // =====================================================
  function restoreBox(card, priceBox) {
    if (origBoxHTML.has(priceBox)) {
      priceBox.innerHTML = origBoxHTML.get(priceBox);
      origBoxHTML.delete(priceBox);
    }
    delete priceBox.dataset.mapRendered;
    removeNotes(card);
  }

  function saveOrig(priceBox) {
    if (priceBox.dataset.mapRendered === undefined && !origBoxHTML.has(priceBox)) {
      origBoxHTML.set(priceBox, priceBox.innerHTML);
    }
  }

  function renderMapPrice(card, priceBox, formatted) {
    const stamp = 'map|' + formatted;
    if (priceBox.dataset.mapRendered === stamp && card.querySelector('.map-pricing-note')) return;

    saveOrig(priceBox);
    priceBox.innerHTML = '<span class="gl-card-price">' + formatted + '</span>';
    priceBox.dataset.mapRendered = stamp;
    addNoteAfter(card, priceBox);
  }

  function renderVariantPrice(card, priceBox, handle, key) {
    const stamp = 'variant|' + handle;
    if (priceBox.dataset.mapRendered === stamp) return;

    // If it was showing a MAP price before, remove the note right away
    if ((priceBox.dataset.mapRendered || '').indexOf('map|') === 0) {
      removeNotes(card);
    }

    getFirstVariant(handle).then(v => {
      if (!v) return;                               // fetch failed -> keep theme default
      if (card.dataset.mapKey !== key) return;      // card was reused for another product
      if (priceBox.dataset.mapRendered === stamp) return;

      const price = centsToDollars(v.price);
      const compare = centsToDollars(v.compare_at_price);
      if (!price) return;

      saveOrig(priceBox);
      if (compare && compare > price) {
        priceBox.innerHTML =
          '<span class="compare-at-price money">' + formatPrice(compare) + '</span> ' +
          '<span class="price price--sale money">' + formatPrice(price) + '</span>';
      } else {
        priceBox.innerHTML = '<span class="gl-card-price">' + formatPrice(price) + '</span>';
      }
      priceBox.dataset.mapRendered = stamp;
      removeNotes(card);
    });
  }

  function processBoxCard(card, type) {
    const priceBox = card.querySelector(type.priceBox);
    if (!priceBox) return;

    const titleEl = card.querySelector(type.title);
    const linkEl = card.querySelector(type.link);
    let cardTitle = titleEl ? titleEl.textContent.trim() : '';
    if (!cardTitle && linkEl) cardTitle = (linkEl.getAttribute('title') || linkEl.textContent || '').trim();
    const cardHref = linkEl ? linkEl.getAttribute('href') : '';

    const key = cardTitle + '|' + cardHref;
    if (card.dataset.mapKey !== undefined && card.dataset.mapKey !== key) {
      restoreBox(card, priceBox);   // card reused for a different product
    }
    card.dataset.mapKey = key;

    const match = findMatch(cardTitle, cardHref);
    const formatted = match ? formatPrice(match.price) : '';

    if (formatted) {
      renderMapPrice(card, priceBox, formatted);
      return;
    }

    const handle = handleFromHref(cardHref);
    if (handle) {
      renderVariantPrice(card, priceBox, handle, key);
    } else {
      restoreBox(card, priceBox);
    }
  }

  // =====================================================
  //  MODE "inline": collection pages (.spf-product-card)
  // =====================================================
  function applyInline(card, type, priceEl, formatted) {
    if (card.dataset.mapOrigPrice === undefined) card.dataset.mapOrigPrice = priceEl.textContent;
    if (priceEl.textContent.trim() !== formatted) priceEl.textContent = formatted;

    card.querySelectorAll(type.oldPrice).forEach(el => {
      if (!el.contains(priceEl) && !el.classList.contains('map-hide-old')) el.classList.add('map-hide-old');
    });

    addNoteAfter(card, (type.noteAfter && card.querySelector(type.noteAfter)) || priceEl);
    card.dataset.mapApplied = '1';
  }

  function restoreInline(card, type) {
    if (card.dataset.mapApplied !== '1') return;
    const priceEl = card.querySelector(type.price);
    if (priceEl && card.dataset.mapOrigPrice !== undefined) priceEl.textContent = card.dataset.mapOrigPrice;
    card.querySelectorAll('.map-hide-old').forEach(el => el.classList.remove('map-hide-old'));
    removeNotes(card);
    delete card.dataset.mapApplied;
    delete card.dataset.mapOrigPrice;
  }

  function processInlineCard(card, type) {
    const titleEl = card.querySelector(type.title);
    const linkEl = card.querySelector(type.link);
    const priceEl = card.querySelector(type.price);
    if (!priceEl) return;

    let cardTitle = titleEl ? titleEl.textContent.trim() : '';
    if (!cardTitle && linkEl) cardTitle = (linkEl.getAttribute('title') || linkEl.textContent || '').trim();
    const cardHref = linkEl ? linkEl.getAttribute('href') : '';

    const key = cardTitle + '|' + cardHref;
    if (card.dataset.mapKey !== undefined && card.dataset.mapKey !== key) restoreInline(card, type);
    card.dataset.mapKey = key;

    const match = findMatch(cardTitle, cardHref);
    const formatted = match ? formatPrice(match.price) : '';

    if (formatted) applyInline(card, type, priceEl, formatted);
    else restoreInline(card, type);
  }

  // ---------- Process all ----------
  function processAll() {
    buildProductMaps(); // ok if data is missing: non-MAP path still works

    CARD_TYPES.forEach(type => {
      document.querySelectorAll(type.card).forEach(card => {
        if (card.dataset.mapType && card.dataset.mapType !== type.name) return;
        card.dataset.mapType = type.name;
        if (type.mode === 'box') processBoxCard(card, type);
        else processInlineCard(card, type);
      });
    });
  }

  // ---------- Debounced runner ----------
  let timer = null;
  function schedule() {
    if (timer) return;
    timer = setTimeout(() => { timer = null; processAll(); }, 50);
  }

  // ---------- Observe DOM ----------
  function observe() {
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        const t = m.target;
        if (t && t.nodeType === 1 && t.classList && t.classList.contains('map-pricing-note')) continue;

        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.classList && node.classList.contains('map-pricing-note')) continue;
          if (node.matches && node.matches(ALL_CARD_SELECTOR)) { schedule(); return; }
          if (node.querySelector && node.querySelector(ALL_CARD_SELECTOR)) { schedule(); return; }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ---------- Init ----------
  function init() {
    injectCSS();
    observe();
    processAll();

    // Retry a few times in case #map-product-data / cards load a bit late
    let attempts = 0;
    const iv = setInterval(() => {
      attempts++;
      processAll();
      if (attempts >= 10) clearInterval(iv);
    }, 500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();